<script setup>
/**
 * @description 全局移动端 Toast 容器，提供信息、成功、失败、警告与加载反馈。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import {
  IconCheckCircleFill,
  IconClose,
  IconCloseCircleFill,
  IconExclamationCircleFill,
  IconLoading,
} from '@arco-design/web-vue/es/icon'
import { Toast, toastItems } from '~/utils/toast'

const positions = ['top', 'center', 'bottom']
const positionLabels = {
  top: '顶部提示通知',
  center: '居中提示通知',
  bottom: '底部提示通知',
}
const typeIcons = {
  success: IconCheckCircleFill,
  error: IconCloseCircleFill,
  warn: IconExclamationCircleFill,
  loading: IconLoading,
}

const toastGroups = computed(() =>
  positions.map((position) => ({
    position,
    label: positionLabels[position],
    items: toastItems.filter((toast) => toast.direction === position),
  }))
)
const isInteractionBlocked = computed(() => toastItems.some((toast) => toast.disableBodyTouch))
const hostRef = ref(null)
const backgroundStates = new Map()
let bodyOverflow = null

function unlockBackgroundInteraction() {
  backgroundStates.forEach((wasInert, element) => {
    element.inert = wasInert
  })
  backgroundStates.clear()

  if (bodyOverflow !== null) {
    document.body.style.overflow = bodyOverflow
    bodyOverflow = null
  }
}

function lockBackgroundInteraction() {
  const host = hostRef.value
  if (!host?.parentElement) return

  bodyOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'

  Array.from(host.parentElement.children).forEach((element) => {
    if (element === host) return
    backgroundStates.set(element, element.inert)
    element.inert = true
  })
}

watch(
  isInteractionBlocked,
  (blocked) => {
    if (blocked) lockBackgroundInteraction()
    else unlockBackgroundInteraction()
  },
  { flush: 'post' }
)

onBeforeUnmount(unlockBackgroundInteraction)

function resolveIcon(toast) {
  return toast.icon || typeIcons[toast.type] || null
}

function resolveRole(type) {
  return type === 'error' || type === 'warn' ? 'alert' : 'status'
}

function resolveLiveMode(type) {
  return type === 'error' || type === 'warn' ? 'assertive' : 'polite'
}
</script>

<template>
  <div
    ref="hostRef"
    class="base-toast-host"
    :class="{ 'base-toast-host--blocking': isInteractionBlocked }"
    data-testid="toast-host"
  >
    <section
      v-for="group in toastGroups"
      v-show="group.items.length"
      :key="group.position"
      class="base-toast-host__region"
      :class="`base-toast-host__region--${group.position}`"
      :aria-label="group.label"
    >
      <TransitionGroup name="base-toast" tag="div" class="base-toast-host__stack">
        <div
          v-for="toast in group.items"
          :key="toast.id"
          class="base-toast"
          :class="[
            `base-toast--${toast.type}`,
            `base-toast--${toast.layout}`,
            { 'base-toast--text-only': !resolveIcon(toast) },
            toast.className,
          ]"
          :role="resolveRole(toast.type)"
          :aria-live="resolveLiveMode(toast.type)"
          aria-atomic="true"
          @mouseenter="Toast.pause(toast.id)"
          @mouseleave="Toast.resume(toast.id)"
        >
          <component
            :is="resolveIcon(toast)"
            v-if="resolveIcon(toast)"
            class="base-toast__icon"
            :class="{ 'base-toast__icon--loading': toast.type === 'loading' }"
            aria-hidden="true"
          />
          <span v-if="toast.content" class="base-toast__content">{{ toast.content }}</span>
          <button
            v-if="toast.closable"
            type="button"
            class="base-toast__close"
            aria-label="关闭提示"
            @click="Toast.close(toast.id)"
          >
            <IconClose aria-hidden="true" />
          </button>
        </div>
      </TransitionGroup>
    </section>
  </div>
</template>

<style scoped lang="scss">
.base-toast-host {
  position: fixed;
  inset: 0;
  z-index: $toast-z-index;
  pointer-events: none;

  &--blocking {
    pointer-events: auto;
    touch-action: none;
  }

  &__region {
    position: absolute;
    inset-inline: 0;
    display: flex;
    justify-content: center;
    padding-inline: $toast-viewport-padding;
    pointer-events: none;

    &--top {
      top: $toast-top-offset;
    }

    &--center {
      top: 50%;
      transform: translateY(-50%);
    }

    &--bottom {
      bottom: $toast-bottom-offset;
    }
  }

  &__stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $toast-stack-gap;
    width: 100%;
  }
}

.base-toast {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $toast-content-gap;
  min-width: $toast-min-size;
  min-height: $toast-min-size;
  max-width: $toast-max-width;
  padding: $toast-padding;
  border-radius: $toast-radius;
  background-color: var(--base-toast-background, #{$toast-background});
  box-shadow: var(--base-toast-shadow, #{$toast-shadow});
  color: var(--base-toast-text-color, #{$toast-text-color});
  text-align: center;
  pointer-events: auto;

  &--horizontal,
  &--text-only {
    flex-direction: row;
    min-width: 0;
    min-height: 0;
    max-width: $toast-horizontal-max-width;
    padding: $toast-horizontal-padding;
    border-radius: $toast-horizontal-radius;
    text-align: start;
  }

  &__icon {
    width: $toast-icon-size;
    height: $toast-icon-size;
    flex: 0 0 auto;

    &--loading {
      animation: base-toast-spin $toast-loading-duration linear infinite;
    }
  }

  &__content {
    max-width: 100%;
    font-size: $toast-font-size;
    font-weight: $toast-font-weight;
    line-height: $toast-line-height;
    overflow-wrap: anywhere;
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: $toast-close-size;
    height: $toast-close-size;
    margin-inline-end: $toast-close-offset;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
}

.base-toast-enter-active,
.base-toast-leave-active,
.base-toast-move {
  transition:
    opacity $toast-transition-duration ease,
    transform $toast-transition-duration ease;
}

.base-toast-enter-from,
.base-toast-leave-to {
  opacity: 0;
  transform: translateY($toast-motion-offset) scale($toast-motion-scale);
}

@keyframes base-toast-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .base-toast-enter-active,
  .base-toast-leave-active,
  .base-toast-move {
    transition-duration: 0s;
  }

  .base-toast__icon--loading {
    animation-duration: $toast-loading-duration-reduced;
  }
}
</style>
